/* eslint-disable max-lines */
import _, { debounce } from 'underscore'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'

import RthPlot from 'pages/shared/Device/RthPlot'
import Legend from 'pages/Device/ThermostatRthPanel/Legend'
import legendDataParser from 'pages/Device/ThermostatRthPanel/Chart/legendDataparser'
import { getDefaultSelectedSeriesItems } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'
import { GetLegendTooltipData } from 'pages/Device/ThermostatRthPanel/Legend/tooltipDataBuilder'

import GA4 from 'helpers/ga4'
import { chartHeight } from 'helpers/plotly/utils'

import { color } from 'styles/colors'
import { RuntimeHistory } from 'types/RuntimeHistory'

type PlotProps = {
  deviceId: string
  startTime: string
  endTime: string
  timeZone: string
  isZoomed: boolean
  isControlHub: boolean
  isHehm?: boolean
  zoomedCallback: (val: boolean) => void
  freezeMouseHover: boolean
  freezeMouseHoverCallback: (val: boolean) => void
  chartDivId: string
  updateSelectedLegendsCallback: (val: unknown) => void
  showFullScreen: boolean
  rthData?: RuntimeHistory | null
  reportCache?: Record<string, unknown>
}

const DOUBLE_CLICK_LATENCY = 500

const Plot = ({ deviceId, startTime, endTime, timeZone, isControlHub, isHehm = false, isZoomed, rthData = null, zoomedCallback, freezeMouseHover, freezeMouseHoverCallback, chartDivId, updateSelectedLegendsCallback, reportCache = {} }: PlotProps) => {
  const doubleClickEventAtRef = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const defaultSelectedLegendItems = useMemo(() => getDefaultSelectedSeriesItems(deviceId, rthData, isHehm), [deviceId, rthData, isHehm])
  const [selectedLegends, setSelectedLegends] = useState({ ...defaultSelectedLegendItems })
  const [isChartHovered, setIsChartHovered] = useState(false)
  const [mouseCursorPosition, setMouseCursorPosition] = useState<number | null>(null)
  const [timeAtCursor, setTimeAtCursor] = useState<number | null>(null)
  const [plotlyRevision, setPlotlyRevision] = useState(1) // needed to handle multiple zoom in events
  const [graphElement, setGraphElement] = useState<HTMLElement | undefined>(undefined)
  const [parseLegendData, setParseLegendData] = useState<unknown>(null)
  const [alertsWithInRange, setAlertsWithInRange] = useState<unknown[] | null>(null)
  const [deviceConnectionStatusAlerts, setDeviceConnectionStatusAlerts] = useState<unknown[] | null>(null)

  const onRelayout = useCallback(({ autosize }) => {
    if (doubleClickEventAtRef.current) {
      return
    }

    if (!autosize) {
      zoomedCallback(true)
      setPlotlyRevision(plotlyRevision + 1)
    }
  }, [plotlyRevision, zoomedCallback])

  const onUnhover = useCallback((event) => {
    event.event.preventDefault()
    if (doubleClickEventAtRef.current || event.event.type !== 'mouseout') { return }

    setTimeAtCursor(null)
    setIsChartHovered(false)
  }, [])

  const onHover = useCallback((time, offset) => {
    if (doubleClickEventAtRef.current) { return }

    setIsChartHovered(true)
    setTimeAtCursor(time) // As plotly does not support timezone we are manually converting
    setMouseCursorPosition(offset) // offsetX is the mouse hover position in the graph updating when mouse move
  }, [])

  const onDoubleClick = useCallback(() => {
    doubleClickEventAtRef.current = Date.now()
    freezeMouseHoverCallback(true)
    GA4.sendEvent({ category: 'Customer Detail Page', action: 'RTH Hover Frozen' })
  }, [freezeMouseHoverCallback])

  const onClick = useCallback((timeAtCursor, offset) => {
    const time = Date.now()

    if ((time - (doubleClickEventAtRef.current ?? 0)) > DOUBLE_CLICK_LATENCY) {
      if (doubleClickEventAtRef.current) {
        GA4.sendEvent({ category: 'Customer Detail Page', action: 'RTH Hover Unfrozen' })
      }

      freezeMouseHoverCallback(false)
      doubleClickEventAtRef.current = null

      if (timeAtCursor != null && offset != null) {
        onHover(timeAtCursor, offset)
      }
    }
  }, [freezeMouseHoverCallback, onHover])

  useEffect(() => {
    setIsChartHovered(false)
    setTimeAtCursor(null)
    setMouseCursorPosition(null)
    freezeMouseHoverCallback(false)
    doubleClickEventAtRef.current = null
  }, [plotlyRevision, isZoomed, freezeMouseHoverCallback])

  useEffect(() => {
    if (!isZoomed) {
      zoomedCallback(false)
    }
  }, [isZoomed, zoomedCallback])

  useEffect(() => {
    const observer = new ResizeObserver(debounce(() => setPlotlyRevision(Date.now().valueOf()), 500))
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect() // unsubscribe the event on UI element removed from dom
  }, [])

  useEffect(() => {
    updateSelectedLegendsCallback(selectedLegends)
  }, [updateSelectedLegendsCallback, selectedLegends])

  const onLegendItemSelectAllClick = (series, selectAll) => {
    const updatedSelectedLegends = selectAll ? _.union(selectedLegends.series, series) : _.without(selectedLegends.series, ...series)
    setSelectedLegends({ ...selectedLegends, series: updatedSelectedLegends })
  }

  const onIndoorLegendItemSelectAllClick = (seriesNames, selectAll) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updatedZoneSeries = _.map((rthData as any)?.zones ?? [], (zone) => {
      return { name: zone.name, series: selectAll ? seriesNames : [] }
    })
    setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })
  }

  const onLegendItemClick = (item) => {
    const updatedSelectedLegends = selectedLegends.series.includes(item) ? _.without(selectedLegends.series, item) : _.union(selectedLegends.series, [item])
    setSelectedLegends({ ...selectedLegends, series: updatedSelectedLegends })
  }

  const onIndoorLegendItemClick = (seriesName, zoneName) => {
    const zone = _.find(selectedLegends.zoneSeries, zone => zone.name === zoneName) || { name: zoneName, series: [] }

    zone.series = zone.series.includes(seriesName) ? _.without(zone.series, seriesName) : _.union(zone.series, [seriesName])
    const updatedZoneSeries = [..._.without(selectedLegends.zoneSeries, zone), zone]
    setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })
  }

  const onIndoorZoneClick = (series, zoneName) => {
    const updatedZoneSeries = _.map(selectedLegends.zoneSeries, zone => {
      if (zone.name === zoneName) {
        return { name: zone.name, series: _.isEmpty(zone.series) ? series : [] }
      } else {
        return { name: zone.name, series: zone.series }
      }
    })

    setSelectedLegends({ ...selectedLegends, zoneSeries: updatedZoneSeries })
  }

  const onClickAlertsLegendItem = (item) => {
    const updatedSelectedAlertSeverities = selectedLegends.alerts.includes(item) ? _.without(selectedLegends.alerts, item) : _.union(selectedLegends.alerts, [item])
    setSelectedLegends({ ...selectedLegends, alerts: updatedSelectedAlertSeverities })
  }

  const onClickAlertsLegendAllOrNone = (allSeriesNames, selectAllClicked) => {
    const updatedSelectedAlertSeverities = selectAllClicked ? allSeriesNames : []
    setSelectedLegends({ ...selectedLegends, alerts: updatedSelectedAlertSeverities })
  }

  const onLegendDataUpdated = useCallback((legendData, alerts, deviceConnectionStateAlerts) => {
    setParseLegendData(legendData)
    setAlertsWithInRange(alerts)
    setDeviceConnectionStatusAlerts(deviceConnectionStateAlerts)
  }, [])

  return (
    <>
      <S.ChartContainer ref={containerRef}>
        <RthPlot
          {...{
            deviceId,
            chartDivId,
            startTime,
            endTime,
            timeZone,
            isZoomed,
            rthData,
            selectedLegends,
            plotlyRevision,
            freezeMouseHover,
            isHehm
          }}
          onClickCallback={onClick}
          onHoverCallback={onHover}
          onUnhoverCallback={onUnhover}
          onRelayoutCallback={onRelayout}
          onDoubleClickCallback={onDoubleClick}
          updateGraphElementCallback={(element) => setGraphElement(element as HTMLElement)}
          onLegendDataCallback={onLegendDataUpdated}
        />

        {isChartHovered && (
          <S.ToolTipCrossHair left={mouseCursorPosition} width={2} height={chartHeight(graphElement) + 10}>
            <S.ArrowDropUpIcon $top={chartHeight(graphElement) - 25} />
          </S.ToolTipCrossHair>
        )}
      </S.ChartContainer>
      {!!parseLegendData &&
        <Legend
          timeAtCursor={timeAtCursor}
          selectedLegends={selectedLegends}
          isChartHovered={isChartHovered}
          isControlHub={isControlHub}
          isHehm={isHehm}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          alertsInVisibleRange={alertsWithInRange as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          deviceConnectionStatusAlerts={deviceConnectionStatusAlerts as any}
          isServerlessRth={chartDivId === 'plotly-serverless-rth-chart'}
          onLegendItemClickCallback={onLegendItemClick}
          onIndoorLegendItemClickCallback={onIndoorLegendItemClick}
          onLegendItemSelectAllClickCallback={onLegendItemSelectAllClick}
          onIndoorLegendItemSelectAllClickCallback={onIndoorLegendItemSelectAllClick}
          onIndoorZoneClickCallback={onIndoorZoneClick}
          onClickAlertsLegendItem={onClickAlertsLegendItem}
          onClickAlertsLegendAllOrNone={onClickAlertsLegendAllOrNone}
          isLegendItemHasData={legendDataParser.isLegendItemHasData}
          timeZone={timeZone}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          legendData={parseLegendData as any}
          legendDataForTimeAtCursor={timeAtCursor ? GetLegendTooltipData(timeAtCursor / 1000, rthData) : null}
          deviceId={deviceId}
          reportCache={reportCache}
        />
      }
    </>
  )
}

export default memo(Plot)

const S = {
  ChartContainer: styled.div`
    text-align: center;
    position: relative;
    margin: 0 auto 40px auto;
    width: 95%;
    pointer-events: none;
  `,
  ArrowDropUpIcon: styled(ArrowDropUpIcon).withConfig({
    shouldForwardProp: (prop) => !['$top'].includes(prop)
  })<{ $top?: number }>`
    font-size: 75px;
    margin-left: -38px;
    position: absolute;
    pointer-events: none;
    top:  ${({ $top }) => (`${$top}px`)};
  `,
  ToolTipCrossHair: styled.div.withConfig({
    shouldForwardProp: (prop) => !['left', 'width', 'height'].includes(prop)
  })<{ left?: number | null; width?: number; height?: number }>`
    background-color: ${color('rthLegendBorderHover')};
    height: ${({ height }) => (`${height}px`)};
    width: ${({ width }) => (`${width}px`)};
    pointer-events: none;
    position: absolute;
    left: ${({ left, width }) => (`${(left ?? 0) - ((width ?? 0) * 0.5)}px`)};
    top: 0;
    z-index: 1;
  `
}
